/* Created by Interactive 63 - https://interactive63.com */
/**
 * The application store: immutable project and editor state updated through Immer recipes, with
 * undo history, grouped transactions and change-filtered subscriptions. All UI changes go
 * through here so history, autosave and the preview see every change.
 */
import { freeze, produce } from 'immer';
import { defaultEditorState } from '../../core/model/defaults';
import type { EditorState, Project } from '../../core/model/types';
import { History } from './history';

export interface AppState {
  project: Project;
  editor: EditorState;
}

export interface SelectOptions {
  /** How values are compared: `Object.is` (default) or a shallow comparison of keys/items. */
  equality?: 'is' | 'shallow';
  /** Call the listener straight away with the current value. */
  immediate?: boolean;
}

interface Subscription {
  check: (state: AppState) => void;
}

/** Shallow equality for arrays and plain objects, falling back to `Object.is`. */
export function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;
  return keys.every((key) => Object.hasOwn(right, key) && Object.is(left[key], right[key]));
}

export class Store {
  private state: AppState;
  private readonly history = new History();
  private subscriptions: Subscription[] = [];
  private transactionDepth = 0;
  private flushing = false;
  private dirty = false;

  /** Creates a store holding the given project and a fresh editor state. */
  constructor(project: Project) {
    this.state = { project: freeze(project, true), editor: defaultEditorState() };
  }

  /** Returns the current state. */
  getState(): AppState {
    return this.state;
  }

  /**
   * Applies a recipe to the project and records it in undo history. Updates that share a
   * coalesce key within a short window merge into one undo step. Recipes that change nothing
   * are ignored.
   */
  updateProject(
    label: string,
    recipe: (draft: Project) => void,
    options?: { coalesceKey?: string },
  ): void {
    const before = this.state.project;
    const changed = produce(before, (draft) => {
      recipe(draft);
    });
    if (changed === before) return;
    const next = produce(changed, (draft) => {
      draft.updatedAt = new Date().toISOString();
    });
    this.state = { ...this.state, project: next };
    if (this.transactionDepth === 0) {
      this.history.record(label, before, Date.now(), options?.coalesceKey);
    }
    this.notify();
  }

  /**
   * Applies a recipe to the project without an undo entry or an updatedAt change. For derived
   * data such as measured path lengths that is not a user edit.
   */
  updateProjectSilently(recipe: (draft: Project) => void): void {
    const next = produce(this.state.project, (draft) => {
      recipe(draft);
    });
    if (next === this.state.project) return;
    this.state = { ...this.state, project: next };
    this.notify();
  }

  /** Applies a recipe to editor-only state. Never recorded in history. */
  updateEditor(recipe: (draft: EditorState) => void): void {
    const next = produce(this.state.editor, (draft) => {
      recipe(draft);
    });
    if (next === this.state.editor) return;
    this.state = { ...this.state, editor: next };
    this.notify();
  }

  /**
   * Replaces the whole project, for import and load. History is cleared unless `keepHistory`
   * is set, in which case the replacement becomes an undoable step.
   */
  replaceProject(project: Project, options?: { keepHistory?: boolean }): void {
    const before = this.state.project;
    this.state = { ...this.state, project: freeze(project, true) };
    if (options?.keepHistory) this.history.record('Replace project', before, Date.now());
    else this.history.clear();
    this.pruneSelection();
    this.notify();
  }

  /**
   * Applies changes for a preview. Nothing is added to undo history, and the returned function
   * puts the project back as it was. If the project has been edited since, reverting does nothing,
   * so a person's own edits are never thrown away.
   */
  previewTransaction(fn: () => void): () => void {
    const before = this.state.project;
    this.transactionDepth += 1;
    try {
      fn();
    } catch (error) {
      this.state = { ...this.state, project: before };
      throw error;
    } finally {
      this.transactionDepth -= 1;
    }
    const during = this.state.project;
    this.notify();
    return () => {
      if (this.state.project !== during) return;
      this.state = { ...this.state, project: before };
      this.pruneSelection();
      this.notify();
    };
  }

  /**
   * Subscribes to a derived value. The listener runs only when the selected value changes and
   * receives the new and previous values. Returns an unsubscribe function.
   */
  select<T>(
    selector: (state: AppState) => T,
    listener: (value: T, prev: T) => void,
    options?: SelectOptions,
  ): () => void {
    const equal = options?.equality === 'shallow' ? shallowEqual : Object.is;
    let last = selector(this.state);
    const subscription: Subscription = {
      check: (state) => {
        const value = selector(state);
        if (equal(value, last)) return;
        const prev = last;
        last = value;
        listener(value, prev);
      },
    };
    this.subscriptions.push(subscription);
    if (options?.immediate) listener(last, last);
    return () => {
      this.subscriptions = this.subscriptions.filter((s) => s !== subscription);
    };
  }

  /** Reverts the most recent history entry. */
  undo(): void {
    const earlier = this.history.undo(this.state.project);
    if (earlier) this.restore(earlier);
  }

  /** Reapplies the most recently undone entry. */
  redo(): void {
    const later = this.history.redo(this.state.project);
    if (later) this.restore(later);
  }

  /** Whether there is a change to undo. */
  canUndo(): boolean {
    return this.history.canUndo();
  }

  /** Whether there is an undone change to redo. */
  canRedo(): boolean {
    return this.history.canRedo();
  }

  /** Label of the change undo would revert, for tooltips. */
  undoLabel(): string | undefined {
    return this.history.undoLabel();
  }

  /** Label of the change redo would reapply, for tooltips. */
  redoLabel(): string | undefined {
    return this.history.redoLabel();
  }

  /**
   * Groups the updates made inside `fn` into a single undo step, notifying listeners once at the
   * end. If `fn` throws, the project is rolled back and the error is rethrown.
   */
  transaction(label: string, fn: () => void): void {
    const before = this.state.project;
    this.transactionDepth += 1;
    try {
      fn();
    } catch (error) {
      this.state = { ...this.state, project: before };
      throw error;
    } finally {
      this.transactionDepth -= 1;
    }
    if (this.transactionDepth > 0) return;
    if (this.state.project !== before) this.history.record(label, before, Date.now());
    this.notify();
  }

  /** Installs a project from history and drops selection of things that no longer exist. */
  private restore(project: Project): void {
    this.state = { ...this.state, project };
    this.pruneSelection();
    this.notify();
  }

  /** Removes selected element and keyframe IDs that are not in the current project. */
  private pruneSelection(): void {
    const { project, editor } = this.state;
    const keyframeIds = new Set(project.tracks.flatMap((t) => t.keyframes.map((kf) => kf.id)));
    const elementIds = editor.selection.elementIds.filter((id) => id in project.elements);
    const keptKeyframes = editor.selection.keyframeIds.filter((id) => keyframeIds.has(id));
    if (
      elementIds.length === editor.selection.elementIds.length &&
      keptKeyframes.length === editor.selection.keyframeIds.length
    ) {
      return;
    }
    this.state = {
      ...this.state,
      editor: produce(editor, (draft) => {
        draft.selection.elementIds = elementIds;
        draft.selection.keyframeIds = keptKeyframes;
      }),
    };
  }

  /** Whether a listener changed state while notifications were running. */
  private hasPendingNotification(): boolean {
    return this.dirty;
  }

  /**
   * Tells subscribers the state changed. Notifications raised while listeners are running are
   * queued and processed afterwards rather than recursing.
   */
  private notify(): void {
    if (this.transactionDepth > 0) return;
    if (this.flushing) {
      this.dirty = true;
      return;
    }
    this.flushing = true;
    try {
      do {
        this.dirty = false;
        for (const subscription of [...this.subscriptions]) subscription.check(this.state);
      } while (this.hasPendingNotification());
    } finally {
      this.flushing = false;
    }
  }
}
